import {Localized, useLocalization} from "@fluent/react";
import {
	Accordion,
	AccordionActions,
	AccordionDetails,
	AccordionSummary,
	Chip,
	Container,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Skeleton,
	Stack,
	Typography,
	styled,
} from "@mui/material";
import React, {useEffect, useMemo, useRef, useState} from "react";

import ContentItemTitle from "../analytics/content/ContentItemTitle";
import ContentIssueTypeFilter from "./ContentIssueTypeFilter";
import type ContentItem from "./ContentItem";
import parseDate from "../../helpers/parseDate";
import useDateTimeFormat from "../../i18n/useDateTimeFormat";
import UserAvatar from "../nav/UserAvatar";
import type ResolutionStatus from "./ResolutionStatus";
import ResolutionStatusFilter from "./ResolutionStatusFilter";
import type ContentIssueSearchResult from "../../store/courses/ContentIssueSearchResult";
import type ContentIssueType from "../../store/courses/ContentIssueType";
import contentIssueService from "../../store/services/contentIssueService";
import type ContentIssueResolutionStatement from "../../store/courses/ContentIssueResolutionStatement";
import useSnackbar from "../../store/ui/useSnackbar";
import type User from "../../store/users/User";
import HtmlContent from "../../utils/HtmlContent";
import flattenRawHtml from "../../utils/HtmlContent/flattenRawHtml";
import LoadingError from "../../utils/errors/LoadingError";
import randomN from "../../utils/randomN";
import ProgressButton from "../../utils/ProgressButton";
import ServerPaginationControls from "../../utils/tables/ServerPaginationControls";
import usePaginationState from "../../utils/tables/usePaginationState";

function IssueReportsView(props: {
	courseId: number;

	contentItem: ContentItem;
	filters: {
		type: ContentIssueType[];
		resolutionStatus: ResolutionStatus[];
	};

	closeAction: React.ReactNode;
}) {
	const {contentItem} = props;

	const {l10n} = useLocalization();
	const showSnackbar = useSnackbar();

	const [pageSize, setPageSize] = useState(10);

	const [selectedStatuses, setSelectedStatuses] = useState(
		props.filters.resolutionStatus
	);

	const [selectedTypes, setSelectedTypes] = useState(props.filters.type);

	const {
		page: reportsPage,
		fetchFirstPage,
		fetchRelatedPage,
		pageFetchStatus,
		retryFetching,
		reloadPage,
	} = usePaginationState<
		ContentIssueSearchResult,
		string,
		{reporter: Map<number, User>}
	>();

	const reports = reportsPage.content;

	const timeout = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		if (contentItem.id === 0) {
			return () => {};
		}

		timeout.current = setTimeout(
			() => {
				fetchFirstPage(() =>
					contentIssueService.searchContentIssues(
						props.courseId,
						{
							chapterId: contentItem.chapterId,
							exerciseId: contentItem.id,
							type: selectedTypes,
							resolutionStatus: selectedStatuses,
						},
						{field: "reportingTime", descending: true},
						pageSize
					)
				);

				clearTimeout(timeout.current);
			},
			timeout.current ? 500 : 0
		);

		return () => {
			clearTimeout(timeout.current);
		};
	}, [
		contentItem.chapterId,
		contentItem.id,
		fetchFirstPage,
		pageSize,
		props.courseId,
		selectedStatuses,
		selectedTypes,
	]);

	function resolveIssue(
		id: string,
		statement: ContentIssueResolutionStatement
	) {
		return contentIssueService
			.resolveContentIssue(props.courseId, id, {statement})
			.then(() => reloadPage())
			.catch(() => showSnackbar("error", l10n.getString("error-general")));
	}

	function cancelResolution(id: string) {
		return contentIssueService
			.cancelContentIssueResolution(props.courseId, id)
			.then(() => reloadPage())
			.catch(() => showSnackbar("error", l10n.getString("error-general")));
	}

	const notFetched = pageFetchStatus !== "succeeded";

	const unknownReporter: User = {
		id: 0,
		name: l10n.getString(
			"content-issues-widget-issue-view-unknown-reporter-name"
		),
	};

	return (
		<Stack
			spacing={2}
			sx={{
				height: "100%",
				minWidth: 600,
				background: (theme) => theme.palette.background.paper,
			}}
		>
			<Stack
				direction="row"
				sx={{
					justifyContent: "space-between",
					py: 2,
					pl: 2,
					pr: 0.5,
					background: (theme) => theme.palette.action.hover,
				}}
			>
				<ContentItemTitle
					primary={contentItem.title}
					secondary={contentItem.subtitle}
					link={contentItem.contentPath}
				/>

				{props.closeAction}
			</Stack>

			<Stack spacing={1} sx={{px: 2}}>
				<ResolutionStatusFilter
					value={selectedStatuses}
					onChange={setSelectedStatuses}
				/>

				<ContentIssueTypeFilter
					value={selectedTypes}
					onChange={setSelectedTypes}
				/>
			</Stack>

			<Stack sx={{overflow: "auto", py: 1, px: 0.5, height: 1}} tabIndex={-1}>
				{pageFetchStatus === "pending" && reports.length === 0 && (
					<>
						<IssueReportPlaceholder />
						<IssueReportPlaceholder />
						<IssueReportPlaceholder />
					</>
				)}

				{pageFetchStatus === "failed" && (
					<LoadingError
						description={
							<Localized id="content-issues-widget-issue-view-error-fetch-issues">
								Something has gone wrong, and we cannot load issue reports
							</Localized>
						}
						variant="block"
						slotProps={{container: {sx: {height: 1}}}}
						onReload={retryFetching}
					/>
				)}

				{pageFetchStatus === "succeeded" && reports.length === 0 && (
					<EmptyState />
				)}

				{reports.map((r) => {
					const reporter =
						reportsPage.related?.reporter.get(r.reporterId) ?? unknownReporter;

					return (
						<Report
							key={r.id}
							reporterName={reporterName(reporter)}
							reporterAvatar={
								<UserAvatar
									user={{
										userName: reporter.name,
										firstName: reporter.firstName,
										lastName: reporter.lastName,
									}}
									size="small"
								/>
							}
							reportingTime={r.reportingTime}
							reportType={r.type}
							reportContent={r.description}
							resolution={r.resolution}
							onResolve={(s) => resolveIssue(r.id, s)}
							onCancelResolution={() => cancelResolution(r.id)}
						/>
					);
				})}
			</Stack>

			<ServerPaginationControls
				disabledControls={{
					first: notFetched || !reportsPage.request.first,
					next: notFetched || !reportsPage.request.next,
					previous: notFetched || !reportsPage.request.previous,
				}}
				pageSize={pageSize}
				label={
					<Localized id="content-issues-widget-issue-view-label-results-per-page">
						Results per page
					</Localized>
				}
				onPageSizeChange={setPageSize}
				onClick={fetchRelatedPage}
			/>
		</Stack>
	);
}

function EmptyState() {
	return (
		<Container
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
				height: "100%",
			}}
		>
			<Typography variant="h6">
				<Localized id="content-issues-widget-issue-view-no-results">
					No issues
				</Localized>
			</Typography>
			<Typography>
				<Localized id="content-issues-widget-issue-view-no-results-descr">
					No issues were found matching your search criteria. Try to adjust
					filters
				</Localized>
			</Typography>
		</Container>
	);
}

function Report(props: {
	reporterName: string;
	reporterAvatar: React.ReactNode;
	reportingTime: string;
	reportType: ContentIssueType;
	reportContent: string;
	resolution: ContentIssueSearchResult["resolution"];

	onResolve: (statement: ContentIssueResolutionStatement) => Promise<void>;
	onCancelResolution: () => Promise<void>;
}) {
	const [expanded, setExpanded] = useState(false);

	const flattenedContent = useMemo(() => flattenRawHtml(props.reportContent), [
		props.reportContent,
	]);

	const [inProgress, setInProgress] = useState<
		"" | "mark_not_resolved" | "mark_irrelevant" | "mark_fixed"
	>("");

	return (
		<Accordion elevation={3} onChange={(_, expanded) => setExpanded(expanded)}>
			<AccordionSummary
				sx={{
					"&:hover": {
						backgroundColor: (theme) => theme.palette.action.hover,
					},
				}}
			>
				<ReportMessageSummary
					reportContent={expanded ? null : flattenedContent}
					reportingTime={props.reportingTime}
					reportType={props.reportType}
					reporterAvatar={props.reporterAvatar}
					reporterName={props.reporterName}
					resolution={props.resolution}
				/>
			</AccordionSummary>

			<AccordionDetails>
				<Typography component="div">
					<HtmlContent content={props.reportContent} />
				</Typography>
			</AccordionDetails>

			<AccordionActions>
				{props.resolution ? (
					<ProgressButton
						inProgress={inProgress === "mark_not_resolved"}
						onClick={async () => {
							setInProgress("mark_not_resolved");
							await props.onCancelResolution();
							setInProgress("");
						}}
					>
						<Localized id="content-issues-widget-issue-view-action-mark-not-resolved">
							Mark as not resolved
						</Localized>
					</ProgressButton>
				) : (
					<>
						<ProgressButton
							inProgress={inProgress === "mark_irrelevant"}
							disabled={Boolean(inProgress)}
							onClick={async () => {
								setInProgress("mark_irrelevant");
								await props.onResolve("irrelevant");
								setInProgress("");
							}}
						>
							<Localized id="content-issues-widget-issue-view-action-mark-irrelevant">
								Mark as irrelevant
							</Localized>
						</ProgressButton>
						<ProgressButton
							inProgress={inProgress === "mark_fixed"}
							disabled={Boolean(inProgress)}
							onClick={async () => {
								setInProgress("mark_fixed");
								await props.onResolve("fixed");
								setInProgress("");
							}}
						>
							<Localized id="content-issues-widget-issue-view-action-mark-fixed">
								Mark as fixed
							</Localized>
						</ProgressButton>
					</>
				)}
			</AccordionActions>
		</Accordion>
	);
}

function ReportMessageSummary(props: {
	reporterName: string;
	reporterAvatar: React.ReactNode;
	reportingTime: string;
	reportType: ContentIssueType;
	reportContent: string | null;
	resolution: ContentIssueSearchResult["resolution"];
}) {
	const formatDateTime = useDateTimeFormat();

	return (
		<Stack
			direction="row"
			sx={{
				justifyContent: "space-between",
				flexGrow: 1,
				minHeight: (theme) => `calc(${theme.spacing(4)} + 2rem)`,
			}}
		>
			<ListItem component="div" disablePadding>
				<Avatar>{props.reporterAvatar}</Avatar>
				<ListItemText
					primary={props.reporterName}
					secondary={props.reportContent}
					primaryTypographyProps={{noWrap: true, maxWidth: "32vw"}}
					secondaryTypographyProps={{noWrap: true, maxWidth: "32vw"}}
				/>
			</ListItem>

			<Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
				{props.resolution && (
					<Chip
						label={
							<Localized
								id="content-issues-widget-issue-view-resolution"
								vars={{statement: props.resolution.statement}}
							>
								{props.resolution}
							</Localized>
						}
						variant="outlined"
					/>
				)}

				<Stack>
					<Typography variant="caption" noWrap align="right">
						{formatDateTime(parseDate(props.reportingTime))}
					</Typography>
					<Typography variant="caption" noWrap align="right">
						<Localized
							id="content-issues-widget-issue-type"
							vars={{type: props.reportType}}
						>
							{props.reportType}
						</Localized>
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
}

function IssueReportPlaceholder() {
	return (
		<Accordion expanded={false}>
			<AccordionSummary>
				<Stack
					direction="row"
					sx={{
						justifyContent: "space-between",
						flexGrow: 1,
					}}
				>
					<ListItem component="div" disablePadding>
						<Avatar>
							<Skeleton variant="circular" width={32} height={32} />
						</Avatar>
						<ListItemText
							primary={
								<Skeleton
									variant="text"
									width={`${randomN(10, 20)}ch`}
									sx={{maxWidth: "100%"}}
								/>
							}
							secondary={
								<Skeleton
									variant="text"
									width={`${randomN(20, 50)}ch`}
									sx={{maxWidth: "100%"}}
								/>
							}
						/>
					</ListItem>

					<Stack sx={{alignItems: "flex-end"}}>
						<Skeleton>
							<Typography variant="caption" noWrap>
								02/05/2025, 12:23
							</Typography>
						</Skeleton>
						<Skeleton>
							<Typography variant="caption">technical</Typography>
						</Skeleton>
					</Stack>
				</Stack>
			</AccordionSummary>
		</Accordion>
	);
}

const Avatar = styled(ListItemAvatar)(({theme}) => ({
	minWidth: theme.spacing(6),
}));

function reporterName(r: User) {
	let name = r.name;
	if (r.firstName || r.lastName) {
		name =
			`${r.firstName ? `${r.firstName} ` : ""}` +
			`${r.lastName ? `${r.lastName} ` : ""}` +
			"/ " +
			name;
	}

	return name;
}

export default IssueReportsView;
